import type { ChurchMembershipSummary } from '@/stores/church-store';

export interface ChurchOrgGroup {
  /** Head church id when present; otherwise the shared parent id of branch-only memberships. */
  orgId: string;
  name: string;
  logoUrl: string | null;
  campuses: ChurchMembershipSummary[];
}

/**
 * Groups memberships under one organisation so the select screen shows one card per
 * church, with campuses listed inside — not a flat list of identical tenant rows.
 */
export const groupChurchMemberships = (
  memberships: ChurchMembershipSummary[],
): ChurchOrgGroup[] => {
  const byOrg = new Map<string, ChurchMembershipSummary[]>();

  for (const row of memberships) {
    const orgId = row.church.parentChurchId ?? row.church.id;
    const list = byOrg.get(orgId) ?? [];
    list.push(row);
    byOrg.set(orgId, list);
  }

  return [...byOrg.entries()]
    .map(([orgId, campuses]) => {
      const sorted = [...campuses].sort((a, b) => {
        if (!a.church.parentChurchId && b.church.parentChurchId) return -1;
        if (a.church.parentChurchId && !b.church.parentChurchId) return 1;
        return a.church.campusName.localeCompare(b.church.campusName);
      });
      const head = sorted.find((row) => !row.church.parentChurchId) ?? sorted[0]!;

      return {
        orgId,
        name: head.church.name,
        logoUrl: head.church.logoUrl,
        campuses: sorted,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const defaultCampusId = (
  group: ChurchOrgGroup,
  lastChurchId: string | null,
): string => {
  if (lastChurchId && group.campuses.some((row) => row.church.id === lastChurchId)) {
    return lastChurchId;
  }
  return group.campuses[0]!.church.id;
};
